import { Association, type Group, type Mission, type Opportunity, type Team } from "@entities";
import {
  Button,
  createTheme,
  FormLabel,
  Menu,
  MenuItem,
  MenuList,
  Stack,
  ThemeProvider,
  Typography,
} from "@mui/material";
import {
  associationRepo,
  groupRepo,
  opportunityRepo,
  teamRepo,
} from "@repositories";
import { useMuiMenu } from "@shared/frontend/hooks";
import { getFutureOpenOpportunitiesWhere } from "@shared/helpers/opportunity-admin-tab-filters";
import {
  formatOpportunitySchedule,
} from "@shared/helpers/format-opportunity-schedule";
import { OrganizationRoles } from "@shared/Roles";
import React, { useCallback, useEffect } from "react";
import FormAutocompleteField from "../../../components/form-components/form-autocomplete/FormAutocompleteField";
import { TwoCheckbox } from "../../../components/two-checkbox/TwoCheckbox";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import PhoneNumberCell from "../../tables/PhoneNumberCell";
import UserNameAndPhoto from "../../tables/users-table/volunteers/components/UserNameAndPhoto";

interface Connections {
  teamId?: string;
  groupId?: string;
  associationId?: string;
  opportunityId?: string;
  isAidRelated?: boolean;
}

interface ConnectionsInfo {
  team?: Team;
  group?: Group;
  association?: Association;
  opportunity?: Opportunity;
}

interface MissionConnectionFieldProps {
  mission?: Partial<Mission>;
  onChange?: (connections: Connections) => void;
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#05204CF2",
    },
  },
});

const HEB_KEYS = [
  { content: "פרויקט", value: "teamId" },
  { content: "קבוצה מאורגנת", value: "groupId" },
  { content: "מתנדב/מבקש סיוע", value: "associationId" },
  { content: "משימה", value: "opportunityId" },
] as const;

const MissionConnectionField: React.FC<MissionConnectionFieldProps> = (
  props,
) => {
  const { mission, onChange } = props;
  const [connections, setConnections] = React.useState<Connections>({});
  const [connectionsInfo, setConnectionsInfo] = React.useState<ConnectionsInfo>(
    {},
  );
  const [menuRef, menuProps, menuActions] = useMuiMenu<HTMLButtonElement>();
  const { currOrganization } = useOrganization();
  const firstRender = React.useRef(true);

  useEffect(() => {
    const connectionKeys = [
      "teamId",
      "groupId",
      "associationId",
      "opportunityId",
    ] as const;

    connectionKeys.forEach((key) => {
      if (mission?.[key]) {
        setConnections((prev) => ({ ...prev, [key]: mission[key] }));
      }
    });
  }, [mission]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    onChange?.(connections);
  }, [JSON.stringify(connections)]);

  const renderConnection = useCallback(
    (key: keyof typeof connections, value?: string) => {
      if (key === "teamId")
        return value ? (
          <Stack>{connectionsInfo.team?.name || mission?.team?.name}</Stack>
        ) : (
          <FormAutocompleteField
            id={key}
            fetchOptions={(search) =>
              teamRepo.find({
                where: {
                  name: { $contains: search },
                  organization: currOrganization,
                },
              })
            }
            getOptionLabel={(option) => option.name}
            onChange={(selected) => {
              setConnections((prev) => ({ ...prev, teamId: selected?.id }));
              setConnectionsInfo((prev) => ({
                ...prev,
                team: selected || undefined,
              }));
            }}
          />
        );
      if (key === "groupId")
        return value ? (
          <Stack>{connectionsInfo.group?.name || mission?.group?.name}</Stack>
        ) : (
          <FormAutocompleteField
            id={key}
            fetchOptions={(search) =>
              groupRepo.find({
                where: {
                  name: { $contains: search },
                  organization: currOrganization,
                  volunteerOrganizationId: null,
                },
              })
            }
            getOptionLabel={(option) => option.name}
            onChange={(selected) => {
              setConnections((prev) => ({ ...prev, groupId: selected?.id }));
              setConnectionsInfo((prev) => ({
                ...prev,
                group: selected || undefined,
              }));
            }}
          />
        );
      if (key === "associationId") {
        const association = connectionsInfo.association || mission?.association;
        return value ? (
          <Stack direction="row" alignItems="center" gap={1}>
            <UserNameAndPhoto user={association?.user} />
            {" | "}
            <PhoneNumberCell phoneNumber={association?.user?.phoneNumber || ""} />
            {association?.roles.includes(OrganizationRoles.Requesting) &&
              association.roles.includes(OrganizationRoles.Volunteer) && (
                <TwoCheckbox
                  yesOptionText="מבקש סיוע"
                  noOptionText="מתנדב"
                  isChecked={!!connections.isAidRelated}
                  handleChange={(event) =>
                    setConnections((prev) => ({
                      ...prev,
                      isAidRelated: event.target.name === "yes" ? true : false,
                    }))
                  }
                />
              )}
          </Stack>
        ) : (
          <FormAutocompleteField
            id={key}
            fetchOptions={(search) =>
              associationRepo.find({
                where: Association.hyperFilter({
                  organizationId: currOrganization?.id,
                  user: { $is: { name: { $contains: search } } },
                }),
                include: {
                  user: true,
                },
              })
            }
            getOptionLabel={(option) => option.user?.name || ""}
            onChange={(selected) => {
              setConnections((prev) => ({
                ...prev,
                associationId: selected?.id,
                isAidRelated: selected?.roles.includes(
                  OrganizationRoles.Requesting,
                ),
              }));
              setConnectionsInfo((prev) => ({
                ...prev,
                association: selected || undefined,
              }));
            }}
          />
        );
      }
      if (key === "opportunityId") {
        const opportunity = connectionsInfo.opportunity || mission?.opportunity;

        return value ? (
          <Stack direction="row" gap={0.5}>
            <Typography>{opportunity?.title}</Typography>
            {" | "}
            <Typography>
              {opportunity ? formatOpportunitySchedule(opportunity) : "ללא זמן"}
            </Typography>
          </Stack>
        ) : (
          <FormAutocompleteField
            id={key}
            fetchOptions={(search) =>
              opportunityRepo.find({
                where: {
                  title: { $contains: search },
                  $and: [
                    getFutureOpenOpportunitiesWhere(currOrganization),
                    {
                      status: {
                        $ne: "AID_REQUEST",
                      },
                    },
                  ],
                },
              })
            }
            getOptionLabel={(option) =>
              `${option.title} ${formatOpportunitySchedule(option)}`
            }
            onChange={(selected) => {
              setConnections((prev) => ({
                ...prev,
                opportunityId: selected?.id,
              }));
              setConnectionsInfo((prev) => ({
                ...prev,
                opportunity: selected || undefined,
              }));
            }}
          />
        );
      }
    },
    [
      currOrganization,
      JSON.stringify(connectionsInfo),
      connections.isAidRelated,
      mission,
    ],
  );

  function handleDelete(key: keyof typeof connections) {
    setConnections((prev) => {
      const newConnections = { ...prev };
      delete newConnections[key];
      return newConnections;
    });
    props.onChange?.(connections);
  }

  return (
    <ThemeProvider theme={theme}>
      <Stack className="field-container">
        <FormLabel sx={{ cursor: "pointer" }} className="field-label">
          שיוך
        </FormLabel>
        <Stack
          border="1px solid rgba(0, 0, 0, 0.23)"
          sx={{ borderRadius: "1rem" }}
          padding={1}
          gap={1}
        >
          {Object.entries(connections).length > 0 &&
            Object.entries(connections).map(([k, value]) => {
              const key = k as keyof typeof connections;
              if (key === "isAidRelated") return null; // Skip isAidRelated as it's not a direct connection
              return (
                <Stack
                  key={key}
                  padding={1}
                  flex={1}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  border="1px solid rgba(5, 32, 76, 0.5)"
                  borderRadius="0.8rem"
                >
                  <Stack gap={0.4}>
                    <Typography fontWeight={520} color="primary">
                      {HEB_KEYS.find(({ value }) => value === key)?.content}
                      {": "}
                    </Typography>
                    {renderConnection(key, value)}
                  </Stack>
                  <Button onClick={() => handleDelete(key)} color="error">
                    הסרה
                  </Button>
                </Stack>
              );
            })}
          <Button
            sx={{ borderRadius: "0.8rem" }}
            color="primary"
            variant="outlined"
            ref={menuRef}
            onClick={menuActions.toggle}
          >
            הוספת שיוך
          </Button>
          <Menu
            {...menuProps}
            slotProps={{
              paper: { sx: { width: menuRef.current?.clientWidth } },
            }}
          >
            <MenuList>
              {HEB_KEYS.filter(
                ({ value }) => !Object.keys(connections).includes(value),
              ).map(({ content, value }) => (
                <MenuItem
                  onClick={() => {
                    setConnections((prev) => ({ ...prev, [value]: "" }));
                    menuActions.hide();
                  }}
                  key={value}
                >
                  {content}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </Stack>
      </Stack>
    </ThemeProvider>
  );
};

export default MissionConnectionField;
